import { getGoogleAnalyticsClientId } from '@service-initiation/shared-clients-google';
import { HttpStatusCodes } from '@service-initiation/shared-clients-http';
import { logger } from '@service-initiation/shared-clients-osp-logger';
import {
  AQLeadSource,
  type CreateAppointmentClientRequest,
  type CreateAppointmentResponse,
  RepairId,
  ServiceTypeCode,
} from '@service-initiation/shared-clients-tech-care-services';
import { queryClient } from '@service-initiation/shared-utils-query-client';

import { techCareServicesClient } from './clients/techCareServicesClient';
import { ErrorType } from './types';

export const createAppointment = async (
  request: CreateAppointmentClientRequest
): Promise<CreateAppointmentResponse> => {
  let googleAnalyticsClientId: string | undefined;

  try {
    googleAnalyticsClientId = getGoogleAnalyticsClientId();
  } catch (err) {
    logger.error(err as Error);
  }

  const body = {
    appointment_at: request.appointmentDateAndTimeUTC,
    aqlead_source_id: AQLeadSource.AsurionDotCom,
    aqlead_type_id: request.aqLeadType,
    clientId: googleAnalyticsClientId,
    company_id: request.companyId,
    country_code: request.countryCode ?? 'us',
    customer: {
      address_line_1: request.customer.addressLine1,
      address_line_2: request.customer.addressLine2,
      can_call: !!request.customer.phoneNumber,
      can_email: true,
      can_email_marketing: request.subscribeToMarketingEmails,
      can_sms: !!request.customer.phoneNumber,
      city: request.customer.city,
      country: request.country ?? 'US',
      email: request.customer.email,
      family_name: request.customer.lastName,
      given_name: request.customer.firstName,
      phone: request.customer.phoneNumber,
      postal_code: request.customer.postalCode,
      state: request.customer.state,
    },
    device_repair_id:
      request.deviceRepairId ?? RepairId.OtherDeviceDiagnosticService,
    device_type_id: request.deviceTypeId,
    intent_id: request.intentId,
    is_appointment: request.isInStoreAppointment ?? true,
    locale: 'en_us',
    message: request.customerMessage,
    referer: 'my.asurion.com/tech-care/repairs',
    service_options: {
      service_type_code: request.serviceTypeCode ?? ServiceTypeCode.CARRY_IN,
    },
    store_id: request.storeId,
  };

  try {
    const createAppointmentRes = await queryClient.fetchQuery({
      queryFn: async () => {
        const response = await techCareServicesClient.createAppointment({
          ...body,
        });

        if (response.status === HttpStatusCodes.InternalServerError) {
          const err = new Error(
            'Internal Server error occurred while trying to create appointment.'
          );
          logger.error(err, {
            data: {
              response: {
                data: response.body,
                status: response.status,
              },
            },
          });
          throw err;
        } else if (response.status === HttpStatusCodes.BadRequest) {
          const err = new Error(ErrorType.SlotIsTakenError);
          logger.info(err, {
            data: {
              response: {
                data: response.body,
                status: response.status,
              },
            },
          });
          throw err;
        }

        return response.body;
      },
      queryKey: ['createAppointment', body],
      retry: 2,
      staleTime: Infinity,
    });

    return {
      error: createAppointmentRes.data.error,
      errorCode: createAppointmentRes.data.code,
      id: createAppointmentRes.data.id,
      shipping_label: createAppointmentRes.data.shipping_label,
      store_info: createAppointmentRes.data.store_info,
    };
  } catch (e) {
    const err = e as Error;
    if (err.message !== ErrorType.SlotIsTakenError) {
      err.message = 'Error occurred while trying to create appointment lead.';
    }
    logger.error(err, {
      data: body,
    });
    throw err;
  }
};
